import { useEffect, useState } from "react";
import {
  CreateOrganizerService,
  getOrganizerEvents,
  GetOrganizersListService,
  toggleOrganizerStatus,
  UpdateOrganizerAccessLevel,
} from "../../../apis/organizers";
import { Modal, Table } from "../../../components/ui";
import { Button, Input, Switch } from "antd";
import { tostE, tostS } from "../../../config/toastConfig";
import { useSelector } from "react-redux";
import OrganizerForm from "../../../components/organizers/OrganizerForm";

export default function OrganizersPage() {
  const { user } = useSelector((state) => state.auth);
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [organizerEvents, setOrganizerEvents] = useState([]);
  const [showEventsModal, setShowEventsModal] = useState(false);

  const organizerEventsColumns = [
    {
      title: "S.No",
      key: "serial",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Event Name",
      dataIndex: "event_name",
      key: "event_name",
    },
    {
      title: "Hashtag",
      dataIndex: "hastag",
      key: "hastag",
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
    },
  ];

  const getOrganizers = async (payload = {}) => {
    setLoading(true);
    try {
      const res = await GetOrganizersListService(payload);
      if (res.status === 200 && res.data.success) {
        setData(res.data.organizers_list);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getOrganizers();
  }, []);

  const handleApplyFilter = () => {
    const q = (searchTerm || "").trim();
    if (q.length > 0) {
      getOrganizers({ search: q });
    } else {
      getOrganizers();
    }
  };

  const handleTogglePrivileges = async (id, checked) => {
    setLoading(true);
    try {
      // Update the access level: 1 = true, 0 = false
      const updated = {
        user_id: id,
        access_level: checked ? 1 : 0,
      };

      // You should create a new API like `UpdateOrganizerAccessLevelService`
      const res = await UpdateOrganizerAccessLevel(updated); // or UpdateOrganizerService(updated)

      if (res.status === 200 && res.data.success) {
        tostS(res.data.message);
        getOrganizers(); // refresh table
      } else {
        tostE(res.data.message);
      }
    } catch (error) {
      console.error(error);
      tostE(error.response.data.message);
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (value, record) => (
        <div>
          <div>{value}</div>
          {record?.user_type ? (
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              ({record.user_type})
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Mobile",
      key: "mobile",
      render: (_, record) =>
        `${record?.code ? record.code + " " : ""}${record?.mobile_number ?? ""}`,
    },
    ...(user?.access_level === 1
      ? [
          {
            title: "Event Privileges",
            key: "access_level",
            render: (_, record) => (
              <Switch
                size="small"
                loading={loading}
                checked={record?.access_level === 1}
                onChange={(checked) =>
                  handleTogglePrivileges(record?.id, checked)
                }
              />
            ),
          },
        ]
      : []),
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Switch
          size="small"
          loading={loading}
          checked={record?.status === "Active"}
          onChange={async (checked) => {
            handleToggleStatus(record?.id, checked);
          }}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button type="default" onClick={() => handleViewEvents(record?.id)}>
          View Events
        </Button>
      ),
    },
    // {
    //   title: "Action",
    //   dataIndex: "action",
    //   key: "action",
    // },
  ];

  const handleCreateOrganizer = async (values) => {
    try {
      const res = await CreateOrganizerService(values);
      if (res.status === 200 && res.data.success) {
        tostS(res.data.message);
        setShowModal(false);
        getOrganizers();
      } else {
        tostE(res.data.message);
      }
    } catch (error) {
      tostE(error?.response?.data?.message || "Failed to add organizer");
    }
  };

  const handleToggleStatus = async (id, checked) => {
    setLoading(true);
    try {
      const res = await toggleOrganizerStatus(id, checked ? 1 : 0);

      if (res.status === 200 && res.data.success) {
        tostS(res.data.message);
        getOrganizers();
      } else {
        tostE(res.data.message);
      }
    } catch (error) {
      console.error(error);
      tostE(error.response.data.message);
    }
  };

  const handleViewEvents = async (id) => {
    setLoading(true);
    try {
      const res = await getOrganizerEvents(id);
      if (res.status === 200 && res.data.success) {
        setOrganizerEvents(res.data.data);
        setShowEventsModal(true);
      } else {
        tostE(res.data.message);
      }
    } catch (error) {
      console.error(error);
      tostE(
        error?.response?.data?.message || "Failed to fetch organizer events",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center">
        <h3 className="page-title m-0">Organizers</h3>
        <Button
          type="primary"
          onClick={() => setShowModal(true)}
          icon={<i className="fa-solid fa-plus"></i>}
        >
          Add Organizer
        </Button>
      </div>
      <div className="main-content">
        <div className="card custom-card">
          <div
            className="d-flex align-items-center gap-2 mb-3"
            style={{ minWidth: 360 }}
          >
            <Input
              allowClear
              placeholder="Search organizers"
              value={searchTerm}
              onChange={(e) => {
                const val = e.target.value;
                setSearchTerm(val);
                if (val.trim().length === 0) {
                  // If search cleared, fetch full list
                  getOrganizers();
                }
              }}
              onPressEnter={handleApplyFilter}
              style={{ width: "260px" }}
            />
            <Button
              type="default"
              loading={loading}
              onClick={handleApplyFilter}
              icon={<i className="fa-solid fa-filter"></i>}
            />
          </div>
          <Table loading={loading} data={data} columns={columns} />
        </div>
      </div>
      <Modal
        modalTitle={"Add Organizer"}
        open={showModal}
        hideFooter={true}
        handleCancel={() => {
          setShowModal(false);
        }}
      >
        <OrganizerForm
          showAccessToggle={user?.access_level === 1}
          onSubmit={handleCreateOrganizer}
          onCancel={() => setShowModal(false)}
        />
      </Modal>
      <Modal
        modalTitle={"Organizer Events"}
        open={showEventsModal}
        hideFooter={true}
        handleCancel={() => {
          setShowEventsModal(false);
          setOrganizerEvents([]);
        }}
        className="w-50"
      >
        <Table
          loading={loading}
          data={organizerEvents}
          columns={organizerEventsColumns}
        />
      </Modal>
    </>
  );
}
